import { FC, useMemo, useState } from "react";
import { TableCell, TableRow } from "../ui/table";
import { formatDate } from "../../lib/helpers/date_helpers";
import { Button } from "../ui/button";
import { Download } from "lucide-react";
import axiosClient from "../../lib/axiosClient";
import saveAs from "file-saver";
import { toast } from "sonner";
import { Abonnement } from "../../lib/interfaces/abonnement.interface";
import { User } from "../../lib/interfaces/user.interface";
import DemanderSiretDialog from "../dialogs/DemanderSiretDialog";

interface Props {
  abonnement: Abonnement;
  user?: User;
}

const AttestationCiteoRowItem: FC<Props> = ({ abonnement, user }) => {
  const afficherCiteo = useMemo(() => abonnement.dateDebut <= "2024-12-01", [abonnement]);
  const afficherLeko = useMemo(() => abonnement.dateFin > "2025-01-01", [abonnement]);
  const [loadingAttestation, setLoadingAttestation] = useState(false);
  const facturesImpayees = abonnement.factures.filter((fac) => fac.payee === false);
  const [openSiretDialog, setOpenSiretDialog] = useState(false);

  const telechargerAttestationCiteo = async () => {
    if (!user) return;
    setLoadingAttestation(true);
    try {
      const { data } = await axiosClient.get(`/abonnements/${abonnement.id}/attestation-citeo-file`, {
        responseType: "arraybuffer",
      });
      const blob = new Blob([data], {
        type: "application/pdf",
      });
      saveAs(blob, `attestation-citeo.pdf`);
    } catch (error) {
      toast.error("Une erreur s'est produite.");
      console.error(error);
    }
    setLoadingAttestation(false);
  };

  const telechargerAttestationLeko = async (siret?: string) => {
    if (!user) return;
    // Demander le SIRET
    if (!user.siret && !siret) {
      setOpenSiretDialog(true);
      return;
    }

    // Si tout est ok, télécharger l'attestation
    setLoadingAttestation(true);
    try {
      const { data } = await axiosClient.get(`/abonnements/${abonnement.id}/attestation-leko-file`, {
        responseType: "arraybuffer",
      });
      const blob = new Blob([data], {
        type: "application/pdf",
      });
      saveAs(blob, `attestation-eco-contribution.pdf`);
    } catch (error) {
      toast.error("Une erreur s'est produite.");
      console.error(error);
    }
    setLoadingAttestation(false);
  };

  return (
    <>
      {afficherLeko && (
        <TableRow>
          <TableCell className='font-medium'>Éco-contribution LEKO</TableCell>
          <TableCell>
            Du {new Date(abonnement.dateDebut).getFullYear() === 2024 ? "01/01/2025" : formatDate(abonnement.dateDebut)}{" "}
            au {formatDate(abonnement.dateFin)}
          </TableCell>
          <TableCell>{abonnement.ruches}</TableCell>
          <TableCell className='text-right'>
            {facturesImpayees.length === 0 ? (
              <Button
                onClick={() => telechargerAttestationLeko()}
                loading={loadingAttestation}
                icon={<Download className='w-4 h-4' />}
                disabled={!user}
              />
            ) : (
              <div className='p-1 bg-orange-100 rounded-md text-center'>En attente de paiement</div>
            )}
          </TableCell>
        </TableRow>
      )}
      {afficherCiteo && (
        <TableRow>
          <TableCell className='font-medium'>Éco-contribution CITEO</TableCell>
          <TableCell>
            Du {formatDate(abonnement.dateDebut)} au{" "}
            {new Date(abonnement.dateDebut).getFullYear() === 2024 ? "31/12/2024" : formatDate(abonnement.dateFin)}
          </TableCell>
          <TableCell>{abonnement.ruches}</TableCell>
          <TableCell className='text-right'>
            {facturesImpayees.length === 0 ? (
              <Button
                onClick={telechargerAttestationCiteo}
                loading={loadingAttestation}
                icon={<Download className='w-4 h-4' />}
                disabled={!user}
              />
            ) : (
              <div className='p-1 bg-orange-100 rounded-md text-center'>En attente de paiement</div>
            )}
          </TableCell>
        </TableRow>
      )}
      <DemanderSiretDialog
        userId={user?.id ?? ""}
        open={openSiretDialog}
        setOpen={setOpenSiretDialog}
        onSubmit={(siret) => telechargerAttestationLeko(siret)}
      />
    </>
  );
};

export default AttestationCiteoRowItem;
